import React, { useState } from 'react';
import { 
  Heart, 
  Bookmark, 
  Download, 
  Share2, 
  MessageCircle, 
  Star, 
  Clock, 
  Users, 
  Crown, 
  Coins, 
  ThumbsUp, 
  Camera, 
  Zap,
  TrendingUp,
  Award,
  ChefHat,
  Eye,
  Gift,
  Sparkles,
  Brain,
  BarChart3,
  ImageIcon,
  CheckCircle,
  AlertCircle,
  Timer,
  DollarSign
} from 'lucide-react';
import SocialShareWidget from '../social/SocialShareWidget';
import SponsorshipIntegration from '../social/SponsorshipIntegration';
import { 
  Recipe, 
  RecipeInteraction, 
  UserCookingReview, 
  ZippyAIAnalysisRequest,
  RecipeAnalytics 
} from '../../types/users';

interface SocialRecipeCardProps {
  recipe: Recipe;
  userInteraction?: RecipeInteraction;
  analytics?: RecipeAnalytics;
  onLike: (recipeId: string) => void;
  onSave: (recipeId: string) => void;
  onDownload: (recipeId: string) => void;
  onShare: (recipeId: string) => void;
  onView: (recipeId: string) => void;
  onTip: (recipeId: string) => void;
  onRequestAIAnalysis: (recipeId: string, analysisType: string) => void;
  onMarkAsCooked: (recipeId: string) => void;
}

const SocialRecipeCard: React.FC<SocialRecipeCardProps> = ({
  recipe,
  userInteraction,
  analytics,
  onLike,
  onSave,
  onDownload,
  onShare,
  onView,
  onTip,
  onRequestAIAnalysis,
  onMarkAsCooked
}) => {
  const [showAIAnalysisModal, setShowAIAnalysisModal] = useState(false);
  const [showCookingReviewModal, setShowCookingReviewModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);

  const hasAIAnalysis = recipe.aiAnalysis !== undefined;
  const userHasCooked = userInteraction?.cooked || false;
  const userHasLiked = userInteraction?.liked || false;
  const userHasSaved = userInteraction?.saved || false;

  const AIAnalysisModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const [selectedAnalysis, setSelectedAnalysis] = useState<string>('comprehensive');
    const [priority, setPriority] = useState<string>('standard');

    const analysisOptions = [
      {
        type: 'nutrition_breakdown',
        name: 'Nutrition Analysis',
        description: 'Detailed breakdown of calories, macros, and micronutrients per serving',
        cost: 15,
        time: '2-3 minutes'
      },
      {
        type: 'ingredient_analysis',
        name: 'Ingredient Deep Dive',
        description: 'Flavor profiles, substitutions, seasonality, and cost analysis',
        cost: 20,
        time: '3-4 minutes'
      },
      {
        type: 'cooking_technique',
        name: 'Technique Analysis',
        description: 'Cooking methods, skill assessment, and optimization tips',
        cost: 25,
        time: '4-5 minutes'
      },
      {
        type: 'comprehensive',
        name: 'Complete ZippyAI Analysis',
        description: 'Full nutrition, ingredient, technique, and health insights',
        cost: 50,
        time: '8-10 minutes',
        popular: true
      }
    ];

    const priorityOptions = [
      { type: 'standard', name: 'Standard', multiplier: 1, description: 'Normal processing queue' },
      { type: 'priority', name: 'Priority', multiplier: 1.5, description: '50% faster processing' },
      { type: 'express', name: 'Express', multiplier: 2, description: 'Skip to front of queue' }
    ];

    const selectedOption = analysisOptions.find(opt => opt.type === selectedAnalysis);
    const selectedPriority = priorityOptions.find(opt => opt.type === priority);
    const totalCost = Math.round((selectedOption?.cost || 0) * (selectedPriority?.multiplier || 1));

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <Brain className="w-6 h-6 mr-2 text-purple-600" />
                ZippyAI Recipe Analysis
              </h3>
              <p className="text-gray-600 mt-1">AI-powered insights that help the entire community</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <span className="sr-only">Close</span>
              ✕
            </button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Community Impact</h4>
                <p className="text-sm text-blue-800 mt-1">
                  Your AI analysis request adds valuable data to our community database! 
                  Once processed, these insights become available to everyone, helping 
                  fellow cooks make informed decisions about nutrition and cooking techniques.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <h4 className="font-medium text-gray-900">Choose Analysis Type</h4>
            {analysisOptions.map(option => (
              <div
                key={option.type}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  selectedAnalysis === option.type
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${option.popular ? 'ring-2 ring-purple-200' : ''}`}
                onClick={() => setSelectedAnalysis(option.type)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h5 className="font-medium text-gray-900">{option.name}</h5>
                      {option.popular && (
                        <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                          Most Popular
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center">
                        <Coins className="w-3 h-3 mr-1" />
                        {option.cost} ZippyCoins
                      </span>
                      <span className="flex items-center">
                        <Timer className="w-3 h-3 mr-1" />
                        {option.time}
                      </span>
                    </div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedAnalysis === option.type
                      ? 'border-purple-500 bg-purple-500'
                      : 'border-gray-300'
                  }`}>
                    {selectedAnalysis === option.type && (
                      <div className="w-full h-full rounded-full bg-white scale-50"></div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-4 mb-6">
            <h4 className="font-medium text-gray-900">Processing Priority</h4>
            <div className="grid grid-cols-3 gap-3">
              {priorityOptions.map(option => (
                <button
                  key={option.type}
                  onClick={() => setPriority(option.type)}
                  className={`p-3 border rounded-lg text-center transition-all ${
                    priority === option.type
                      ? 'border-purple-500 bg-purple-50 text-purple-900'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium">{option.name}</div>
                  <div className="text-xs text-gray-600 mt-1">{option.description}</div>
                  {option.multiplier > 1 && (
                    <div className="text-xs text-purple-600 mt-1">
                      +{Math.round((option.multiplier - 1) * 100)}% cost
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="font-medium text-gray-900">Total Cost</div>
                <div className="text-sm text-gray-600">
                  {selectedOption?.name} • {selectedPriority?.name} Priority
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-purple-600 flex items-center">
                  <Coins className="w-5 h-5 mr-1" />
                  {totalCost}
                </div>
                <div className="text-xs text-gray-500">ZippyCoins</div>
              </div>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onRequestAIAnalysis(recipe.id, selectedAnalysis);
                onClose();
              }}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center"
            >
              <Brain className="w-4 h-4 mr-2" />
              Request Analysis
            </button>
          </div>
        </div>
      </div>
    );
  };

  const CookingReviewModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const [rating, setRating] = useState(5);
    const [review, setReview] = useState('');
    const [difficulty, setDifficulty] = useState<'easier' | 'as_expected' | 'harder'>('as_expected');
    const [cookingTime, setCookingTime] = useState(recipe.prep_time + recipe.cook_time);
    const [modifications, setModifications] = useState('');
    const [tips, setTips] = useState('');
    const [photos, setPhotos] = useState<File[]>([]);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-900 flex items-center">
              <ChefHat className="w-6 h-6 mr-2 text-green-600" />
              Share Your Cooking Experience
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              ✕
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overall Rating
              </label>
              <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    className={`text-2xl ${
                      star <= rating ? 'text-yellow-400' : 'text-gray-300'
                    } hover:text-yellow-400`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                How was the difficulty compared to the recipe description?
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'easier', label: 'Easier than expected', color: 'green' },
                  { value: 'as_expected', label: 'As expected', color: 'blue' },
                  { value: 'harder', label: 'Harder than expected', color: 'red' }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => setDifficulty(option.value as any)}
                    className={`p-3 border rounded-lg text-center ${
                      difficulty === option.value
                        ? `border-${option.color}-500 bg-${option.color}-50`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Actual cooking time (minutes)
                </label>
                <input
                  type="number"
                  value={cookingTime}
                  onChange={(e) => setCookingTime(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Photos
                </label>
                <label className="w-full px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 flex items-center justify-center">
                  <Camera className="w-4 h-4 mr-2" />
                  Choose Photos
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => setPhotos(Array.from(e.target.files || []))}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Review
              </label>
              <textarea
                value={review}
                onChange={(e) => setReview(e.target.value)}
                placeholder="How did it turn out? What did you think of the flavors?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Modifications Made (Optional)
              </label>
              <textarea
                value={modifications}
                onChange={(e) => setModifications(e.target.value)}
                placeholder="Did you substitute any ingredients or change the recipe?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tips for Others (Optional)
              </label>
              <textarea
                value={tips}
                onChange={(e) => setTips(e.target.value)}
                placeholder="Any helpful tips or tricks you discovered while making this?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20 resize-none"
              />
            </div>

            {photos.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Selected Photos ({photos.length})
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative">
                      <img
                        src={URL.createObjectURL(photo)}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-20 object-cover rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // Handle review submission
                onClose();
              }}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Share Review
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
      {/* Recipe Image with Overlay Metrics */}
      {recipe.image_url && (
        <div className="relative">
          <img 
            src={recipe.image_url} 
            alt={recipe.name}
            className="w-full h-48 object-cover"
          />
          
          {/* Trending Badge */}
          {analytics && analytics.trendingScore > 50 && (
            <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded-full flex items-center text-xs">
              <TrendingUp className="w-3 h-3 mr-1" />
              Trending
            </div>
          )}
          
          {/* AI Analysis Badge */}
          {hasAIAnalysis && (
            <div className="absolute top-2 right-2 bg-purple-500 text-white px-2 py-1 rounded-full flex items-center text-xs">
              <Brain className="w-3 h-3 mr-1" />
              AI Analyzed
            </div>
          )}
          
          {/* Quick Stats Overlay */}
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white rounded-lg px-2 py-1 text-xs">
            <div className="flex items-center space-x-3">
              <span className="flex items-center">
                <Eye className="w-3 h-3 mr-1" />
                {analytics?.totalViews || 0}
              </span>
              <span className="flex items-center">
                <Bookmark className="w-3 h-3 mr-1" />
                {analytics?.totalSaves || 0}
              </span>
              <span className="flex items-center">
                <ChefHat className="w-3 h-3 mr-1" />
                {analytics?.totalCooks || 0}
              </span>
            </div>
          </div>
        </div>
      )}
      
      <div className="p-4">
        {/* Header with Contributor Info */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 text-lg mb-1">{recipe.name}</h3>
            
            {recipe.contributor && (
              <div className="flex items-center space-x-2 mb-2">
                <img 
                  src={recipe.contributor.avatar} 
                  alt={recipe.contributor.displayName}
                  className="w-5 h-5 rounded-full"
                />
                <span className="text-sm text-gray-600">{recipe.contributor.displayName}</span>
                {recipe.contributor.verificationStatus === 'expert_verified' && (
                  <Crown className="w-3 h-3 text-blue-600" />
                )}
                <span className="text-xs text-gray-500">
                  Level {recipe.contributor.level}
                </span>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => onLike(recipe.id)}
              className={`${userHasLiked ? 'text-red-500' : 'text-gray-400'} hover:text-red-500`}
            >
              <Heart className="w-5 h-5" fill={userHasLiked ? 'currentColor' : 'none'} />
            </button>
            <button 
              onClick={() => onSave(recipe.id)}
              className={`${userHasSaved ? 'text-blue-500' : 'text-gray-400'} hover:text-blue-500`}
            >
              <Bookmark className="w-5 h-5" fill={userHasSaved ? 'currentColor' : 'none'} />
            </button>
          </div>
        </div>
        
        <p className="text-gray-600 text-sm mb-3 line-clamp-2">{recipe.description}</p>
        
        {/* Recipe Stats */}
        <div className="flex items-center space-x-4 mb-3 text-sm text-gray-500">
          <span className="flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            {recipe.prep_time + recipe.cook_time}m
          </span>
          <span className="flex items-center">
            <Users className="w-4 h-4 mr-1" />
            {recipe.servings}
          </span>
          <span className="flex items-center">
            <Star className="w-4 h-4 mr-1 text-yellow-500" />
            {recipe.rating}
          </span>
          {analytics && (
            <span className="flex items-center">
              <MessageCircle className="w-4 h-4 mr-1" />
              {analytics.totalReviews}
            </span>
          )}
        </div>
        
        {/* Community Engagement Metrics */}
        {analytics && (
          <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
            <div className="flex items-center space-x-3">
              <span className="flex items-center">
                <ThumbsUp className="w-3 h-3 mr-1" />
                {analytics.totalLikes}
              </span>
              <span className="flex items-center">
                <Download className="w-3 h-3 mr-1" />
                {analytics.totalDownloads}
              </span>
              <span className="flex items-center">
                <Share2 className="w-3 h-3 mr-1" />
                {analytics.totalShares}
              </span>
            </div>
            
            {analytics.totalZippyCoinsSpentOnAnalysis > 0 && (
              <span className="flex items-center text-purple-600">
                <Coins className="w-3 h-3 mr-1" />
                {analytics.totalZippyCoinsSpentOnAnalysis} community investment
              </span>
            )}
          </div>
        )}
        
        {/* AI Analysis Preview */}
        {hasAIAnalysis && recipe.aiAnalysis && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
            <div className="flex items-center space-x-2 mb-2">
              <Brain className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-900">ZippyAI Insights</span>
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                {recipe.aiAnalysis.confidenceScore}% confidence
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-purple-700 font-medium">Calories:</span>
                <span className="ml-1 text-purple-900">{recipe.aiAnalysis.nutritionBreakdown.perServing.calories}</span>
              </div>
              <div>
                <span className="text-purple-700 font-medium">Skill:</span>
                <span className="ml-1 text-purple-900 capitalize">{recipe.aiAnalysis.techniqueAnalysis.skillLevel}</span>
              </div>
            </div>
            {recipe.aiAnalysis.healthInsights.healthBenefits.length > 0 && (
              <div className="mt-2">
                <span className="text-xs text-purple-700 font-medium">Health Benefits:</span>
                <span className="text-xs text-purple-900 ml-1">
                  {recipe.aiAnalysis.healthInsights.healthBenefits.slice(0, 2).join(', ')}
                </span>
              </div>
            )}
          </div>
        )}
        
        {/* Nutrition Summary */}
        <div className="text-sm text-gray-600 mb-4">
          <div className="flex justify-between">
            <span><strong>{recipe.calories}</strong> cal</span>
            <span><strong>{recipe.protein}g</strong> protein</span>
            {hasAIAnalysis && recipe.aiAnalysis && (
              <span><strong>{recipe.aiAnalysis.nutritionBreakdown.perServing.fiber}g</strong> fiber</span>
            )}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <button 
            onClick={() => onView(recipe.id)}
            className="bg-blue-600 text-white py-2 px-3 rounded text-sm hover:bg-blue-700 flex items-center justify-center"
          >
            <Eye className="w-4 h-4 mr-1" />
            View Recipe
          </button>
          
          {!userHasCooked ? (
            <button 
              onClick={() => setShowCookingReviewModal(true)}
              className="bg-green-600 text-white py-2 px-3 rounded text-sm hover:bg-green-700 flex items-center justify-center"
            >
              <ChefHat className="w-4 h-4 mr-1" />
              I Made This!
            </button>
          ) : (
            <button 
              onClick={() => setShowCookingReviewModal(true)}
              className="bg-green-100 text-green-800 py-2 px-3 rounded text-sm hover:bg-green-200 flex items-center justify-center"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Update Review
            </button>
          )}
        </div>
        
        {/* Secondary Actions */}
        <div className="flex space-x-2">
          <button 
            onClick={() => onDownload(recipe.id)}
            className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded text-sm hover:bg-gray-200 flex items-center justify-center"
          >
            <Download className="w-4 h-4 mr-1" />
            Download
          </button>
          
          <SocialShareWidget
            contentType="recipe"
            contentId={recipe.id}
            contentTitle={recipe.title}
            contentDescription={recipe.description}
            contentImageUrl={recipe.image}
            userReferralCode="USER123" // This would come from user context
            onShare={(platform, shareData) => {
              // Track social sharing for rewards
              console.log('Recipe shared:', platform, shareData);
              onShare(recipe.id);
            }}
            className="flex-1"
          />
          
          {recipe.contributor && (
            <button 
              onClick={() => onTip(recipe.id)}
              className="bg-yellow-500 text-white py-2 px-3 rounded text-sm hover:bg-yellow-600 flex items-center justify-center"
            >
              <Gift className="w-4 h-4 mr-1" />
              Tip
            </button>
          )}
        </div>
        
        {/* AI Analysis Request */}
        {!hasAIAnalysis && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <button 
              onClick={() => setShowAIAnalysisModal(true)}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2 px-3 rounded text-sm hover:from-purple-700 hover:to-blue-700 flex items-center justify-center"
            >
              <Zap className="w-4 h-4 mr-2" />
              Request ZippyAI Analysis
              <span className="ml-2 text-xs bg-white bg-opacity-20 px-2 py-1 rounded">
                Helps Community
              </span>
            </button>
          </div>
        )}

        {/* Sponsorship Integration */}
        <SponsorshipIntegration
          context="recipe"
          contextData={{
            ingredients: recipe.ingredients,
            recipeCategory: recipe.category,
            dietaryPreferences: recipe.dietary_info,
            cookingSkill: hasAIAnalysis ? recipe.aiAnalysis?.techniqueAnalysis.skillLevel : 'intermediate'
          }}
          onSponsorInteraction={(sponsorId, interactionType, data) => {
            // Track sponsor interactions for rewards
            console.log('Sponsor interaction:', sponsorId, interactionType, data);
          }}
          className="mt-3 pt-3 border-t border-gray-200"
        />
      </div>
      
      {/* Modals */}
      {showAIAnalysisModal && (
        <AIAnalysisModal onClose={() => setShowAIAnalysisModal(false)} />
      )}
      
      {showCookingReviewModal && (
        <CookingReviewModal onClose={() => setShowCookingReviewModal(false)} />
      )}
    </div>
  );
};

export default SocialRecipeCard; 
